import cognitoConfig from '../utils/cognitoConfig';
import { loggerStatic as logger } from '../utils/logger';

// Base URL for API calls
const API_BASE_URL = process.env.REACT_APP_API_URL || 'https://api.dozzy.com.au';

// Generate CSRF token for API requests
const generateCsrfToken = () => {
  const array = new Uint8Array(16);
  window.crypto.getRandomValues(array);
  return Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
};

// Get or create CSRF token
const getCsrfToken = () => {
  let token = sessionStorage.getItem('csrfToken');
  if (!token) {
    token = generateCsrfToken();
    sessionStorage.setItem('csrfToken', token);
    logger.debug('Generated new CSRF token');
  }
  return token;
};

export const userService = {
  // Save user signup data with token
  saveUserSignup: async (userData, idToken) => {
    logger.info('Saving user signup data with token');
    
    try {
      // Get CSRF token
      const csrfToken = getCsrfToken();
      
      // Call the API with authentication
      const response = await fetch(`${API_BASE_URL}/users`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save user data: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      logger.error('Error saving user data with token', error);
      
      // For development, store in localStorage as fallback
      if (process.env.NODE_ENV === 'development') {
        logger.warn('Development mode: Storing user data in localStorage');
        localStorage.setItem('userData', JSON.stringify(userData));
      }
      
      throw error;
    }
  },

  // Save user signup data without token (for free signups)
  saveUserSignupWithoutToken: async (userData) => {
    logger.info('Saving free signup user data without token');
    
    try {
      // Get CSRF token
      const csrfToken = getCsrfToken();
      
      // Call the API without authentication for free signups
      const response = await fetch(`${API_BASE_URL}/users/free-signup`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': csrfToken
        },
        body: JSON.stringify(userData),
      });
      
      if (!response.ok) {
        throw new Error(`Failed to save free signup data: ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      logger.error('Error saving free signup data', error);
      
      // For development, store in localStorage as fallback
      if (process.env.NODE_ENV === 'development') {
        logger.warn('Development mode: Storing free signup data in localStorage');
        localStorage.setItem('freeSignupData', JSON.stringify(userData));
      }
      
      throw error;
    }
  },
  
  // Get user data
  getUserData: async (idToken) => {
    try {
      // Extract email from ID token for backup
      let email = null;
      try {
        const decodedToken = JSON.parse(atob(idToken.split('.')[1]));
        email = decodedToken.email;
        logger.debug('Extracted email from token for query parameter');
      } catch (err) {
        logger.error('Error extracting email from token', err);
      }
      
      // Get CSRF token
      const csrfToken = getCsrfToken();
      
      // Add email as query parameter for backup
      const url = email 
        ? `${API_BASE_URL}/users/me?email=${encodeURIComponent(email)}`
        : `${API_BASE_URL}/users/me`;
      
      logger.debug(`Making request to get user data`);
      
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${idToken}`,
          'X-CSRF-Token': csrfToken,
          'Origin': window.location.origin
        }
      });
      
      if (!response.ok) {
        // For 404 errors, throw a specific error that will be handled in the UserContext
        if (response.status === 404) {
          logger.error(`User not found (404)`);
          throw new Error(`User not found (404): ${response.statusText}`);
        }
        throw new Error(`Failed to get user data: ${response.statusText}`);
      }
      
      const data = await response.json();
      logger.debug('User data retrieved successfully');
      return data;
    } catch (error) {
      logger.error('Error getting user data', error);
      throw error;
    }
  }
};
